import type { ApolloError } from "@apollo/client";
import { PubSubSharedActions } from "@selector/shared";
import PubSub from "pubsub-js";
import type { StateCreator } from "zustand";

import { type ActivityInstanceParameter, WorkflowActivityType } from "@/graphql.ts";
import { notify } from "@/modules/notifications/notify";
import { getActivitiesQuery } from "@/modules/workflow/graphql/queries.ts";
import { workflowIntl } from "@/modules/workflow/i18n/intl.ts";
import type {
  WorkflowActivitySlice,
  WorkflowActivitySliceState,
  WorkflowEditorStore,
} from "@/modules/workflow/stores/workflow-editor-store-types";
import {
  extractGetActivitiesData,
  findLatestVersion,
  getValueFromWorkflowParameter,
} from "@/modules/workflow/utils/activity.ts";
import {
  createWorkflowParameter,
  parametersCompatible,
  workflowParamToActivityInstanceParam,
} from "@/modules/workflow/utils/parameters.ts";
import type { Activity, WorkflowParameter } from "@/modules/workflow/utils/types.ts";

export const initialActivitySliceState: WorkflowActivitySliceState = {
  isLoadingActivities: false,
  activities: [],
  activityErrors: [],
};

export const createActivitySlice: StateCreator<WorkflowEditorStore, [], [], WorkflowActivitySlice> = (set, get) => {
  PubSub.subscribe(PubSubSharedActions.LOGOUT, () => {
    set(initialActivitySliceState);
  });

  return {
    ...initialActivitySliceState,
    loadActivities: async () => {
      set({ isLoadingActivities: true });
      // We need to preload activities to fill and validate activities parameters
      const { data: activitiesData, error } = await getActivitiesQuery({});

      if (!error && activitiesData) {
        const activitiesResult = extractGetActivitiesData(activitiesData);
        const activities = activitiesResult.activities;

        if (activities) {
          // We generate all available versions by activity
          const clonedActivities: Activity[] = [...activities];
          for (const activity of clonedActivities) {
            const sameActivities = activities
              .filter((act) => act.name === activity.name)
              .filter((act) => get().hasAdminCapability || act.type !== WorkflowActivityType.Admin);
            const defaultActivities = sameActivities.filter((act) => act.default);
            const relevantActivities = defaultActivities.length >= 1 ? defaultActivities : sameActivities;
            // If the latest version is found, all other version are set to false. This implies that, following
            // semantic versioning, the latest version is treated as the default.
            const latestVersion = findLatestVersion(
              relevantActivities.length > 0 ? relevantActivities.map((act) => act.version.name) : [],
            );
            if (latestVersion) {
              for (const act of sameActivities) {
                act.default = act.version.name === latestVersion;
              }
            }
            activity.versions = sameActivities.map((act) => ({ ...act.version, default: act.default }));
          }

          set({ isLoadingActivities: false, activities: clonedActivities });
        }

        if (activitiesResult.errors) {
          set({ activityErrors: activitiesResult.errors });
        }
      } else {
        set({ isLoadingActivities: false, isLoadingWorkflow: false });
        const __error = new Error(
          (error as ApolloError | undefined)?.message ?? workflowIntl.formatMessage({ id: "an error occurred" }),
        );

        notify.fromErrors({
          title: "Loading Workflow Activities Error",
          errors: [__error],
        });

        throw __error;
      }
    },
    async switchActivityVersion(nodeId, version) {
      const selectedActivity = get().selectedActivity;
      const node = get().getNode(nodeId);

      if (node) {
        const newActivityVersion = get().getActivity(node.data.activityName, version);
        const oldActivityVersion = get().getActivity(node.data.activityName, node.data.version);
        if (!newActivityVersion || !oldActivityVersion) {
          return;
        }

        // 1. Save existing parameters before updating anything
        const existingParametersById = new Map(
          get()
            .getParameters(nodeId)
            .map((p) => [p.parameterId, p]),
        );

        // 2. Update the node with its new version
        set({
          nodes: get().nodes.map((currentNode) => {
            if (currentNode.id === nodeId) {
              // it's important to create a new object here, to inform React Flow about the changes
              // see https://reactflow.dev/learn/advanced-use/state-management
              return {
                ...currentNode,
                data: {
                  ...currentNode.data,
                  version,
                  activityType: newActivityVersion.type,
                },
              };
            }
            return currentNode;
          }),
        });

        // Register all parameters for the new activity version and keep existing values if compatible.
        // This also removes parameters that do not exist anymore
        const compatibleParameters: ActivityInstanceParameter[] = [];

        for (const newParameter of newActivityVersion.parameters ?? []) {
          const existingParam = existingParametersById.get(newParameter.id);
          if (existingParam) {
            const existingValue = await getValueFromWorkflowParameter(
              newParameter,
              workflowParamToActivityInstanceParam(existingParam),
              existingParam?.values.isNothing(),
            );

            const p = createWorkflowParameter(
              nodeId,
              newParameter.id,
              newParameter,
              existingValue,
              existingParam?.staged,
            );

            if (parametersCompatible(existingParam, p, oldActivityVersion, newActivityVersion)) {
              compatibleParameters.push(workflowParamToActivityInstanceParam(p));
            }
          }
        }

        await get().registerActivityParameters(nodeId, compatibleParameters);

        // Enforce parameter validation, e.g. if min/max value changed or Region does not match anymore
        get().checkAllParameters(nodeId);

        // 4. Remove incompatible connections
        const invalidEdges = get()
          .edges.filter((e) => e.target === nodeId || e.source === nodeId)
          .filter((e) => !get().isValidConnection(e, false));
        const invalidEdgeIds = new Set(invalidEdges.map((e) => e.id));

        set({
          edges: get().edges.filter((currentEdge) => !invalidEdgeIds.has(currentEdge.id)),
        });

        if (selectedActivity?.nodeId === nodeId) {
          set({
            selectedActivity: {
              ...selectedActivity,
              version,
            },
          });
        }

        get().pushTimelineData({
          nodes: get().nodes,
          edges: get().edges,
          parameters: get().getParameters(),
        });
      }
    },
    async restoreActivityDefaultParams(nodeId: string, parameterIds?: WorkflowParameter["parameterId"][]) {
      const node = get().getNode(nodeId);
      if (!node) {
        return;
      }

      const activity = get().getActivity(node.data.activityName, node.data.version);

      if (activity) {
        // if no parameters are given reset all of them
        const parametersToReset =
          parameterIds && Array.isArray(parameterIds)
            ? (activity.parameters?.filter((p) => parameterIds?.includes(p.id)) ?? [])
            : (activity.parameters ?? []);

        for (const parameter of parametersToReset) {
          const defaultValues = await getValueFromWorkflowParameter(parameter, undefined, true);
          get().setActivityParameterValue(nodeId, parameter.id, defaultValues);
        }
      }
    },
    getActivity: (activityName, version) => {
      return (
        get().activities.find((activity) => activity.name === activityName && activity.version.name === version) ?? null
      );
    },
  };
};
